import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { API_URL_FELL } from 'config';
import { Camera, CameraRecordingDataRow } from 'lib/types';
import { Stream } from 'stream';

export type CameraPayload = {
    data?: Camera,
    cameras?: CameraRecordingDataRow[], // Use CameraRecordingDataRow instead of creating a new Camera type
    projectId: number,
    siteId: number,
    siteDesignId: number,
    cameraGroupId?: number,
    error?: string
}

export const postCameraThunk = createAsyncThunk<
    CameraPayload,
    { projectId: number, siteId: number, siteDesignId: number },
    { rejectValue: CameraPayload }
>('camera/cameraCreate',
    async (args, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign/${args.siteDesignId}/camera`);
            return response.data;
        } catch (err) {
            const error: AxiosError<string> = err;
            return rejectWithValue({
                error: error.message,
                projectId: args.projectId,
                siteId: args.siteId,
                siteDesignId: args.siteDesignId
            });
        }
    });

export const deleteCameraThunk = createAsyncThunk<
    CameraPayload,
    { projectId: number, siteId: number, siteDesignId: number, cameraGroupId: number },
    { rejectValue: CameraPayload }
>('camera/cameraDelete',
    async (args, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign/${args.siteDesignId}/camera/${args.cameraGroupId}`);
            return response.data;
        } catch (err) {
            const error: AxiosError<string> = err;
            return rejectWithValue({
                error: error.message,
                projectId: args.projectId,
                siteId: args.siteId,
                siteDesignId: args.siteDesignId,
                cameraGroupId: args.cameraGroupId
            });
        }
    });

type DeleteCameraStreamArgs = {
  projectId: number;
  siteId: number;
  siteDesignId: number;
  cameraGroupId: number;
  cameraStreamId: number;
};

type DeleteCameraStreamReject = { error: string };

export const deleteCameraStreamThunk = createAsyncThunk<
  void,                          
  DeleteCameraStreamArgs,        // args
  { rejectValue: DeleteCameraStreamReject } // reject value shape
>(
  "camera/deleteCameraStream",
  async (args, { rejectWithValue }) => {
    try {
      const url = `${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign/${args.siteDesignId}/camera/${args.cameraGroupId}/camerastream/${args.cameraStreamId}`;
      await axios.delete(url);
      return;
    } catch (e) {
      const err = e as AxiosError;
      return rejectWithValue({ error: err.message || "Failed to delete stream" });
    }
  }
);

export const putCameraThunk = createAsyncThunk<CameraRecordingDataRow,
    { projectId: number, siteId: number, siteDesignId: number, cameraGroupId: number, cameras: CameraRecordingDataRow[]},
    { rejectValue: CameraPayload }
>('camera/cameraPut',
    async (args, { rejectWithValue }) => {      
        try {            
            const payload = {
                cameras: args.cameras
            };

            // Make the PUT request with the new payload structure
            const response = await axios.put<CameraRecordingDataRow>(`${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign/${args.siteDesignId}/camera/${args.cameraGroupId}`,
                payload // Use the modified payload here
            );

            return response.data;
        } catch (err) {
            const error: AxiosError<string> = err;
            return rejectWithValue({
                error: error.message,
                projectId: args.projectId,
                siteId: args.siteId,
                siteDesignId: args.siteDesignId,
                cameraGroupId: args.cameraGroupId,
                cameras: [] // Set to an empty array to avoid TypeScript errors
            });
        }
});

export type CameraStreamPayload = {
    data?: Stream,
    projectId: number,
    siteId: number,
    siteDesignId: number,
    cameraGroupId?: number,
    error?: string
}

export const postCameraStreamThunk = createAsyncThunk<
    CameraStreamPayload,
    { projectId: number, siteId: number, siteDesignId: number, cameraGroupId: number },
    { rejectValue: CameraStreamPayload }
>('camera/cameraStreamCreate',
    async (args, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign/${args.siteDesignId}/camera/${args.cameraGroupId}/camerastream`);
            return response.data;
        } catch (err) {
            const error: AxiosError<string> = err;
            return rejectWithValue({
                error: error.message,
                projectId: args.projectId,
                siteId: args.siteId,
                siteDesignId: args.siteDesignId
            });
        }
    });

export const postDuplicateCameraThunk = createAsyncThunk<
    CameraPayload,
    { projectId: number, siteId: number, siteDesignId: number, cameraGroupId: number },
    { rejectValue: CameraPayload }
>('camera/cameraDuplicate',
    async (args, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign/${args.siteDesignId}/camera/${args.cameraGroupId}/copy`);
            return response.data;
        } catch (err) {
            const error: AxiosError<string> = err;
            return rejectWithValue({
                error: error.message,
                projectId: args.projectId,
                siteId: args.siteId,
                siteDesignId: args.siteDesignId
            });
        }
    });

